import type {
  ApprovalKind,
  CheckNeed,
  OperationalAwardTypeCode,
  QueueStep,
  QueueViewId,
  ScanStatus,
} from '@myrewards/contracts';
import { isApiError } from '../api';
import { formatCount, formatMoney, plural, type StatusTone } from '../ui';

// Words the operations screens share. Plain stages and steps only: never an internal state code.

export const TYPE_LABEL: Record<OperationalAwardTypeCode, string> = {
  SDB: 'Sector Discretionary Bonus',
  EDB: 'Executive Discretionary Bonus',
  PAA: 'Professional Achievement Award',
  TAA: 'Team Achievement Award',
  SPOT: 'Spot Award',
};

export const TYPE_SHORT: Record<OperationalAwardTypeCode, string> = {
  SDB: 'Sector Discretionary',
  EDB: 'Executive Discretionary',
  PAA: 'PAA',
  TAA: 'TAA',
  SPOT: 'Spot Award',
};

/** The Stock Admin's plain steps (SA1 "awards by stage"). */
export const STEP: Record<QueueStep, { label: string; tone: StatusTone; none: string }> = {
  waiting_for_claim: { label: 'Waiting for a claim', tone: 'waiting', none: 'Nothing to claim' },
  checking: { label: 'Checking', tone: 'info', none: 'No checks open' },
  letters: { label: 'Letters', tone: 'info', none: 'No letters to prepare' },
  employee_choice: { label: 'Employee’s choice', tone: 'waiting', none: 'No elections open' },
  past_deadline: { label: 'Past deadline', tone: 'problem', none: 'None past deadline' },
  with_payroll: { label: 'With Payroll', tone: 'neutral', none: 'Nothing with Payroll' },
  statement_check: { label: 'Statement check', tone: 'info', none: 'No statements to check' },
  // Counted as awards, like every step: an award at the statement check can still have rows
  // to enter, so a zero here says nothing about rows (APP-067 §13).
  shareworks: { label: 'Shareworks', tone: 'neutral', none: 'None at this step' },
};

/** What a step tile says under a count above zero. */
export const STEP_NOTE: Record<QueueStep, string> = {
  waiting_for_claim: 'Approved, not claimed',
  checking: 'Claimed and being checked',
  letters: 'Ready for letters',
  employee_choice: 'Inside the election window',
  past_deadline: 'Chase, extend or close',
  with_payroll: 'Cash and tax with Payroll',
  statement_check: 'Earning statements to check',
  shareworks: 'Grants being entered',
};

/**
 * What an award at the check still needs before it can be marked checked from the queue
 * (APP-105), in words. Someone else holding it is said by the Claim column, so it has none here.
 */
export const CHECK_NEED: Record<Exclude<CheckNeed, 'claimed'>, string> = {
  lines: 'Needs its people and amounts',
  coding: 'Needs its charge codes',
  proof: 'Needs its approval proof',
  write_up: 'Needs its write-up',
  fmv: 'Share price changed since it was sent: open it to take the values again',
  // APP-151: a PAA's options are counted in the CFO's value of one option.
  option_value: 'Value of one option changed since it was sent: open it to take the values again',
};

/** Why an award stayed in the queue after Mark checked (APP-105). */
export const CHECK_LEFT: Record<'changed' | 'claimed' | 'not_ready' | 'not_found', string> = {
  changed: 'it changed since the list was loaded',
  claimed: 'someone else is on it',
  not_ready: 'it isn’t ready',
  not_found: 'it isn’t yours to check',
};

/**
 * The dedicated screens for the steps after payroll: the statement check and Shareworks, Manage
 * tabs of their own since APP-234 (`/work/…` redirects here).
 */
export const WORK_HREF = {
  earning_statements: '/manage/earning-statements',
  shareworks: '/manage/shareworks',
} as const;

/**
 * The queue's views, by their address (the Stock Admin Home links here too), under Manage › Queue
 * since APP-234 (`/queue/…` redirects here). Earning statements and Shareworks are their own pages.
 */
export const QUEUE_VIEW: Record<QueueViewId, { label: string; href: string }> = {
  queue: { label: 'Queue', href: '/manage/queue' },
  mine: { label: 'My queue', href: '/manage/queue/mine' },
  tax: { label: 'Tax to enter', href: '/manage/queue/tax' },
  in_progress: { label: 'Everything in progress', href: '/manage/queue/in-progress' },
  past_deadline: { label: 'Past deadline', href: '/manage/queue/past-deadline' },
  letters: { label: 'Letters', href: '/manage/queue/letters' },
  earning_statements: { label: 'Earning statements', href: WORK_HREF.earning_statements },
  shareworks: { label: 'Shareworks', href: WORK_HREF.shareworks },
};

export const KIND_LABEL: Record<ApprovalKind, string> = {
  // APP-121: pending historical approvals use ordinary Executive wording.
  self_recognition: 'Executive decision',
  // A delegate's entry, or since APP-106 one by someone named to start Sector bonuses.
  delegate: 'Entered for your approval',
  executive: 'Executive decision',
  overage: 'Over budget',
  record_decision: 'Record the decision',
};

/** "$4,500", "$10,000 + 64 options", "64 options". */
export function amountWords(cashCents: number, options: number): string {
  const parts: string[] = [];
  if (cashCents !== 0 || options === 0) parts.push(formatMoney(cashCents));
  if (options !== 0) parts.push(plural(options, 'option', 'options'));
  return parts.join(' + ');
}

/** OH or G&A, as a charge code's cost type reads. */
export function costTypeWord(costType: 'OH' | 'GA'): string {
  return costType === 'GA' ? 'G&A' : 'OH';
}

/** "64 options", "Cash only" (never a bare dash). */
export function optionsWords(options: number | null): string {
  return options === null || options === 0 ? 'Cash only' : formatCount(options);
}

/** A file's scan status in words, and whether it can be opened. */
export const SCAN: Record<ScanStatus, { label: string; tone: StatusTone; opens: boolean }> = {
  pending: { label: 'Checking for viruses', tone: 'waiting', opens: false },
  clean: { label: 'Checked', tone: 'done', opens: true },
  not_required: { label: 'Ready', tone: 'done', opens: true },
  malicious: { label: 'Blocked: a virus was found', tone: 'problem', opens: false },
  unscannable: { label: 'Couldn’t be checked: upload it again', tone: 'problem', opens: false },
};

/**
 * Plain words for a failed call. The server's problem codes are stable; titles and details carry
 * the specifics (who holds a claim, what the check still needs).
 */
export function problemWords(error: unknown): string {
  if (!isApiError(error)) return 'Something went wrong. Nothing was changed; try again.';
  const detail = error.problem?.detail;
  switch (error.code) {
    case 'already_claimed':
      return `${error.message} Their claim shows in the queue.`;
    case 'not_claimed':
      return 'Nobody is on this award any more. Reload to see where it stands.';
    case 'conflict':
      if (detail === 'decision_proof_required')
        return 'Attach the approval proof first. It counts once the virus check clears it.';
      return 'Someone changed this while you were working. We’ve loaded the latest; check it and try again.';
    case 'check_not_complete':
      return detail ?? 'The check isn’t finished yet.';
    case 'over_budget':
      return 'Taking the values again would put this award over budget, so nothing changed. Only an Executive can approve an overage.';
    case 'budget_not_set':
      return `The ${detail ?? 'pool'} budget isn’t set for this year yet. An Executive sets it in Executive › Budget pools.`;
    case 'award_not_ready':
      return 'Some people or amounts need another look: every included person needs an amount, and the group has to fit the award type.';
    case 'invalid_request':
      return detail ?? 'Some of the details need another look.';
    case 'forbidden':
      return 'You can’t do that on this award.';
    case 'preview_read_only':
      return 'Preview is read-only. Exit preview to make changes.';
    case 'not_found':
      return 'We couldn’t find that award. It may have moved on.';
    case 'file_too_large':
      return 'That file is too big. Files can be up to 25 MB.';
    case 'network_error':
      return 'We couldn’t reach MyRewards. Nothing was changed; try again.';
    default:
      return error.message;
  }
}

/** A stale version, a decision someone else made first: reload the view before anything else. */
export function isStale(error: unknown): boolean {
  return (
    isApiError(error) &&
    error.status === 409 &&
    (error.code === 'conflict' ||
      error.code === 'already_claimed' ||
      error.code === 'not_claimed' ||
      error.code === 'tax_changed')
  );
}
